import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Select } from '@keobook/ui';
import {
  adminApi,
  type AdminAuditEvent,
  type AdminClientDetail,
  type AdminClientListItem,
  type AdminFeatureFlag,
  type AdminReferralCommission,
  type AdminStaffItem,
  type SeatConfig,
  type AiConfig,
} from '../lib/api';
import { Layout } from '../components/Layout';
import { useAuth } from '../lib/auth';
import { Link, Navigate } from 'react-router-dom';

type Tab = 'clients' | 'audit' | 'staff' | 'referrals';

/**
 * Single-page admin portal. Two tabs:
 *
 *   Clients — Partner Admin view, available to both partner (CPA) and
 *             platform_admin. Cross-tenant clients list with search +
 *             drill-in to one client detail (engagement / subscription /
 *             counts / owner). Backed by /v1/admin/clients[/:id].
 *
 *   Staff   — Platform Settings view, available to platform_admin only.
 *             Lists every staff user, lets the operator add another
 *             admin by email + role, and demote (with the server-side
 *             cannot-self + last-platform-admin guards). Backed by
 *             /v1/admin/platform/staff (POST, DELETE).
 *
 * Anyone reaching this URL who isn't staff bounces back to /dashboard —
 * the requireAdmin gate already returns 404 to non-staff at the API
 * level, but the bounce gives a clean UX instead of a flash of empty
 * cards.
 */
export function Admin() {
  const { user } = useAuth();
  const isStaff = user?.kind === 'staff';
  const isPlatformAdmin = user?.staffRole === 'platform_admin';
  const mfaEnrolled = !!user?.mfaEnrolled;
  const [tab, setTab] = useState<Tab>('clients');

  if (!isStaff) return <Navigate to="/dashboard" replace />;

  // Partner+ tier bypasses every tenant's RLS. We block the admin
  // surface for any staff user who hasn't enrolled a second factor —
  // server-side returns 403 on every admin route, and this card makes
  // the requirement discoverable instead of leaving them staring at a
  // toast of API errors.
  if (!mfaEnrolled) {
    return (
      <Layout>
        <div
          className="kb-page"
          style={{ maxWidth: 640, margin: '0 auto', padding: '2rem 1.5rem' }}
        >
          <h1
            style={{
              fontSize: 26,
              fontWeight: 600,
              color: 'var(--kb-gray-900)',
              margin: '0 0 1rem',
            }}
          >
            Admin
          </h1>
          <Card>
            <CardTitle>Two-factor enrollment required</CardTitle>
            <CardSubtitle>
              The admin surface reads across every tenant&apos;s books. A second factor on the
              account that does that is non-negotiable.
            </CardSubtitle>
            <ol style={{ marginTop: '0.75rem', paddingLeft: '1.25rem', fontSize: 13 }}>
              <li style={{ marginBottom: 4 }}>
                Open <Link to="/settings">Settings → Security</Link> and click{' '}
                <strong>Set up authenticator app</strong>.
              </li>
              <li style={{ marginBottom: 4 }}>
                Scan the QR code in your authenticator app, enter the 6-digit code, and confirm.
              </li>
              <li style={{ marginBottom: 4 }}>
                A green panel appears with a <strong>Sign out now</strong> button — click it (the
                current JWT still says <code>mfa=false</code> until a fresh sign-in mints one).
              </li>
              <li>
                Sign back in and reopen <strong>Admin</strong>. The tabs render.
              </li>
            </ol>
            <div style={{ marginTop: '1rem' }}>
              <Link to="/settings">
                <Button>Open Settings → Security</Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 980, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{ fontSize: 26, fontWeight: 600, color: 'var(--kb-gray-900)', margin: '0 0 1rem' }}
        >
          Admin
        </h1>

        <div
          role="tablist"
          aria-label="Admin sections"
          style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}
        >
          <Button
            size="sm"
            variant={tab === 'clients' ? 'primary' : 'ghost'}
            role="tab"
            aria-selected={tab === 'clients'}
            onClick={() => setTab('clients')}
          >
            Partner · Clients
          </Button>
          <Button
            size="sm"
            variant={tab === 'audit' ? 'primary' : 'ghost'}
            role="tab"
            aria-selected={tab === 'audit'}
            onClick={() => setTab('audit')}
          >
            Partner · Audit log
          </Button>
          {isPlatformAdmin && (
            <Button
              size="sm"
              variant={tab === 'staff' ? 'primary' : 'ghost'}
              role="tab"
              aria-selected={tab === 'staff'}
              onClick={() => setTab('staff')}
            >
              Platform · Settings
            </Button>
          )}
          {isPlatformAdmin && (
            <Button
              size="sm"
              variant={tab === 'referrals' ? 'primary' : 'ghost'}
              role="tab"
              aria-selected={tab === 'referrals'}
              onClick={() => setTab('referrals')}
            >
              Platform · Referrals
            </Button>
          )}
        </div>

        {tab === 'clients' && <ClientsTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'staff' && isPlatformAdmin && (
          <>
            <StaffTab currentUserId={user!.id} />
            <SeatConfigPanel />
            <AiConfigPanel />
          </>
        )}
        {tab === 'referrals' && isPlatformAdmin && <ReferralsTab />}
      </div>
    </Layout>
  );
}

// --------------------------------------------------------------------------
// Clients tab
// --------------------------------------------------------------------------

function ClientsTab() {
  const [q, setQ] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [clients, setClients] = useState<AdminClientListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminClientDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [busyAction, setBusyAction] = useState(false);

  const refreshList = () => setReloadKey((k) => k + 1);

  // Debounced search.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      adminApi
        .listClients({ q: q.trim() || undefined, includeArchived, limit: 100 })
        .then((r) => {
          if (!alive) return;
          setClients(r.clients);
          setTotal(r.total);
          setError(null);
        })
        .catch((e: unknown) => {
          if (!alive) return;
          setError(e instanceof Error ? e.message : 'Failed to load clients.');
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, includeArchived, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let alive = true;
    setDetailLoading(true);
    adminApi
      .getClient(selectedId)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load client.');
      })
      .finally(() => {
        if (alive) setDetailLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selectedId]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
      <Card>
        <CardTitle>Clients</CardTitle>
        <CardSubtitle>
          Every client in the system. {total} total{q ? `, filtered by "${q}"` : ''}.
        </CardSubtitle>
        <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
          <Input
            type="search"
            placeholder="Search by legal or display name…"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
          />
        </div>
        <label
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            fontSize: 12,
            marginBottom: '0.5rem',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.currentTarget.checked)}
          />
          Include archived
        </label>
        {error && (
          <div className="kb-form-error" role="alert">
            {error}
          </div>
        )}
        {loading && !clients.length ? (
          <p className="kb-muted">Loading…</p>
        ) : (
          <ul
            style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 540, overflowY: 'auto' }}
          >
            {clients.map((c) => (
              <li
                key={c.id}
                style={{
                  padding: '8px 6px',
                  borderBottom: '1px solid var(--kb-gray-100)',
                  cursor: 'pointer',
                  background: selectedId === c.id ? 'var(--kb-green-50)' : undefined,
                }}
                onClick={() => setSelectedId(c.id)}
              >
                <div style={{ fontWeight: 600 }}>
                  {c.displayName}
                  {c.archivedAt && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'var(--kb-red-50, #fee2e2)',
                        color: 'var(--kb-red-600, #b91c1c)',
                      }}
                    >
                      Archived
                    </span>
                  )}
                </div>
                <div className="kb-muted" style={{ fontSize: 12 }}>
                  {c.legalName} · {planLabel(c.planTier)} · {c.status} ·{' '}
                  {c.ownerEmail ?? '(no owner)'}
                </div>
              </li>
            ))}
            {!loading && clients.length === 0 && (
              <li className="kb-muted" style={{ padding: '8px 6px' }}>
                No clients match.
              </li>
            )}
          </ul>
        )}
      </Card>

      <Card>
        <CardTitle>{detail?.displayName ?? 'Detail'}</CardTitle>
        <CardSubtitle>
          {detail ? detail.legalName : 'Click a row on the left to see the full record.'}
        </CardSubtitle>
        {detailLoading && <p className="kb-muted">Loading…</p>}
        {detail && (
          <dl style={{ marginTop: '0.75rem', fontSize: 13 }}>
            <Detail label="Client id" value={detail.id} mono />
            <Detail label="Plan" value={planLabel(detail.planTier)} />
            <Detail label="Status" value={detail.status} />
            <Detail label="Owner" value={detail.owner?.email ?? '(no owner)'} />
            <Detail label="State" value={detail.stateCode ?? '—'} />
            <Detail label="Created" value={shortDate(detail.createdAt)} />
            <hr
              style={{ border: 0, borderTop: '1px solid var(--kb-gray-100)', margin: '10px 0' }}
            />
            <Detail
              label="Engagement"
              value={detail.engagement ? detail.engagement.id : '(none)'}
              mono
            />
            <Detail
              label="Subscription"
              value={
                detail.subscription
                  ? `${detail.subscription.tier} · ${detail.subscription.status}`
                  : '(none)'
              }
            />
            <Detail
              label="Stripe sub id"
              value={detail.subscription?.stripeSubscriptionId ?? '—'}
              mono
            />
            <hr
              style={{ border: 0, borderTop: '1px solid var(--kb-gray-100)', margin: '10px 0' }}
            />
            <Detail label="Accounts" value={String(detail.counts.accounts)} />
            <Detail label="Documents" value={String(detail.counts.documents)} />
            <Detail label="Proposals (pending)" value={String(detail.counts.proposalsPending)} />
            <Detail label="Journal entries" value={String(detail.counts.journalEntries)} />
            <hr
              style={{
                border: 0,
                borderTop: '1px solid var(--kb-gray-100)',
                margin: '14px 0 10px',
              }}
            />
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 10,
              }}
            >
              <span className="kb-muted" style={{ fontSize: 12 }}>
                Override plan tier:
              </span>
              {detail.subscription && detail.subscription.status !== 'canceled' && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busyAction}
                  onClick={async () => {
                    if (
                      !confirm(
                        `Cancel "${detail.displayName}" subscription? This stops billing immediately. If Stripe is wired, it cancels the Stripe subscription too — otherwise just the local DB.`,
                      )
                    )
                      return;
                    setBusyAction(true);
                    try {
                      const out = await adminApi.cancelClientSubscription(detail.id);
                      const fresh = await adminApi.getClient(detail.id);
                      setDetail(fresh);
                      refreshList();
                      if (out.stripeOutcome === 'failed') {
                        setError(
                          `Local DB updated, but Stripe cancel failed: ${out.stripeError ?? 'unknown'}. Reconcile via Stripe dashboard.`,
                        );
                      }
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Cancel subscription failed.');
                    } finally {
                      setBusyAction(false);
                    }
                  }}
                >
                  Cancel subscription
                </Button>
              )}
              <Select
                value={detail.planTier}
                disabled={busyAction}
                onChange={async (e) => {
                  const next = e.currentTarget.value as
                    | 'starter'
                    | 'professional_plus'
                    | 'business_plus';
                  if (next === detail.planTier) return;
                  if (
                    !confirm(
                      `Override "${detail.displayName}" to ${planLabel(next)}? The next Stripe webhook may resync this back if Stripe disagrees — this is for cases Stripe couldn't reconcile on its own.`,
                    )
                  )
                    return;
                  setBusyAction(true);
                  try {
                    await adminApi.setClientPlanTier(detail.id, next);
                    const fresh = await adminApi.getClient(detail.id);
                    setDetail(fresh);
                    refreshList();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Plan-tier override failed.');
                  } finally {
                    setBusyAction(false);
                  }
                }}
                style={{ minWidth: 160 }}
              >
                <option value="starter">Starter</option>
                <option value="professional_plus">Professional+</option>
                <option value="business_plus">Business+</option>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {detail.archivedAt ? (
                <>
                  <span className="kb-muted" style={{ fontSize: 12 }}>
                    Archived {shortDate(detail.archivedAt)}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busyAction}
                    onClick={async () => {
                      setBusyAction(true);
                      try {
                        await adminApi.restoreClient(detail.id);
                        const fresh = await adminApi.getClient(detail.id);
                        setDetail(fresh);
                        refreshList();
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Restore failed.');
                      } finally {
                        setBusyAction(false);
                      }
                    }}
                  >
                    Restore
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busyAction}
                  onClick={async () => {
                    if (
                      !confirm(
                        `Archive "${detail.displayName}"? It disappears from the default list, the owner can re-onboard, and you can restore it later. No data is destroyed.`,
                      )
                    )
                      return;
                    setBusyAction(true);
                    try {
                      await adminApi.archiveClient(detail.id);
                      const fresh = await adminApi.getClient(detail.id);
                      setDetail(fresh);
                      refreshList();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Archive failed.');
                    } finally {
                      setBusyAction(false);
                    }
                  }}
                >
                  Archive
                </Button>
              )}
            </div>
          </dl>
        )}
      </Card>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 8, padding: '2px 0' }}>
      <dt className="kb-muted" style={{ fontSize: 12 }}>
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontFamily: mono ? 'var(--kb-mono, ui-monospace, SFMono-Regular, monospace)' : undefined,
          fontSize: mono ? 12 : 13,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </dd>
    </div>
  );
}

// --------------------------------------------------------------------------
// Staff tab (platform_admin only)
// --------------------------------------------------------------------------

function StaffTab({ currentUserId }: { currentUserId: string }) {
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <StaffCard currentUserId={currentUserId} />
      <DemoSweepCard />
      <FeatureFlagsCard />
    </div>
  );
}

function StaffCard({ currentUserId }: { currentUserId: string }) {
  const [staff, setStaff] = useState<AdminStaffItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true);
    adminApi
      .listStaff()
      .then((r) => setStaff(r.staff))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load staff.'))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  async function demote(item: AdminStaffItem) {
    if (item.id === currentUserId) return;
    if (!confirm(`Demote ${item.email} back to customer?`)) return;
    setBusyId(item.id);
    setError(null);
    setNotice(null);
    try {
      await adminApi.demoteStaff(item.id);
      setNotice(`${item.email} is no longer staff.`);
      refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Demote failed.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <CardTitle>Staff</CardTitle>
      <CardSubtitle>
        Anyone with staff access to the platform. Add another admin or partner below; demote with
        the button on each row. You cannot change your own role here — ask another platform_admin.
      </CardSubtitle>
      {error && (
        <div className="kb-form-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="kb-toast kb-toast--success" role="status" style={{ marginTop: 8 }}>
          {notice}
        </div>
      )}
      <AddStaffForm onAdded={refresh} />
      <div style={{ marginTop: '1rem' }}>
        {loading ? (
          <p className="kb-muted">Loading…</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--kb-gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>MFA</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Created</th>
                <th style={{ padding: '8px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--kb-gray-100)' }}>
                  <td style={{ padding: '8px 6px' }}>
                    {s.email}
                    {s.id === currentUserId && (
                      <span className="kb-muted" style={{ marginLeft: 6, fontSize: 11 }}>
                        (you)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px 6px' }}>{roleLabel(s.staffRole)}</td>
                  <td style={{ padding: '8px 6px' }}>{s.mfaEnrolledAt ? '✓' : '—'}</td>
                  <td style={{ padding: '8px 6px' }}>{shortDate(s.createdAt)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={s.id === currentUserId || busyId === s.id}
                      onClick={() => void demote(s)}
                    >
                      {busyId === s.id ? '…' : 'Demote'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function AddStaffForm({ onAdded }: { onAdded: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminStaffItem['staffRole']>('partner');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.promoteStaff({ email: email.trim(), staffRole: role });
      setEmail('');
      onAdded();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Add staff failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        marginTop: '1rem',
        padding: '0.875rem',
        background: 'var(--kb-gray-50, #f8fafc)',
        borderRadius: 8,
      }}
    >
      <Field label="Email (existing user)">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          placeholder="cpa@firm.com"
          style={{ minWidth: 280 }}
        />
      </Field>
      <Field label="Role">
        <Select
          value={role}
          onChange={(e) => setRole(e.currentTarget.value as AdminStaffItem['staffRole'])}
        >
          <option value="partner">Partner (CPA)</option>
          <option value="platform_admin">Platform admin (IT)</option>
          <option value="manager">Manager</option>
          <option value="accountant">Accountant</option>
          <option value="reviewer">Reviewer</option>
        </Select>
      </Field>
      <Button type="submit" disabled={busy}>
        {busy ? 'Adding…' : 'Add staff'}
      </Button>
      {error && (
        <span className="kb-form-error" role="alert" style={{ marginLeft: 8 }}>
          {error}
        </span>
      )}
    </form>
  );
}

// Helpers ------------------------------------------------------------------

function planLabel(p: string): string {
  if (p === 'starter') return 'Starter';
  if (p === 'professional_plus') return 'Professional+';
  if (p === 'business_plus') return 'Business+';
  return p;
}
function roleLabel(r: string): string {
  if (r === 'platform_admin') return 'Platform admin';
  if (r === 'partner') return 'Partner';
  return r[0]!.toUpperCase() + r.slice(1);
}
function shortDate(iso: string): string {
  try {
    return iso.slice(0, 10);
  } catch {
    return iso;
  }
}

// --------------------------------------------------------------------------
// Demo-user sweep (platform_admin only)
//
// The public "Try the demo" flow on keobooks.com mints a throwaway
// app_user per session (email = demo-<ts>@demo.local). Each session
// also onboards a sample client + seeds the CoA, so a swept demo user
// takes a whole client tree with it via the existing FK cascades. This
// card shows the current count + sweeps anything older than N days.
// --------------------------------------------------------------------------

function DemoSweepCard() {
  const [olderThanDays, setOlderThanDays] = useState(7);
  const [stats, setStats] = useState<{
    total: number;
    eligible: number;
    cutoffDays: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = (days = olderThanDays) => {
    setLoading(true);
    adminApi
      .getDemoStats(days)
      .then((s) => {
        setStats(s);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load stats.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    refresh(olderThanDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [olderThanDays]);

  async function sweep() {
    if (!stats || stats.eligible === 0) return;
    if (
      !confirm(
        `Permanently delete ${stats.eligible} demo user${stats.eligible === 1 ? '' : 's'} older than ${olderThanDays} day${olderThanDays === 1 ? '' : 's'}, plus their owned clients and all downstream data? This cannot be undone.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await adminApi.sweepDemoUsers(olderThanDays);
      setNotice(
        `Deleted ${r.usersDeleted} demo user${r.usersDeleted === 1 ? '' : 's'} and ${r.clientsDeleted} client${r.clientsDeleted === 1 ? '' : 's'}.`,
      );
      refresh(olderThanDays);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Sweep failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Demo users — sweep</CardTitle>
      <CardSubtitle>
        Public &ldquo;Try the demo&rdquo; sessions create a throwaway user + client per visit. Sweep
        old ones to keep the system roster clean. Real customers (any other email pattern) are never
        touched.
      </CardSubtitle>
      {error && (
        <div className="kb-form-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="kb-toast kb-toast--success" role="status" style={{ marginTop: 8 }}>
          {notice}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          marginTop: '0.875rem',
          padding: '0.875rem',
          background: 'var(--kb-gray-50, #f8fafc)',
          borderRadius: 8,
        }}
      >
        <Field label="Older than (days)">
          <Input
            type="number"
            min={1}
            max={365}
            value={olderThanDays}
            onChange={(e) => setOlderThanDays(Math.max(1, Number(e.currentTarget.value) || 7))}
            style={{ width: 90 }}
          />
        </Field>
        <div style={{ marginBottom: 6, fontSize: 13 }}>
          {loading ? (
            <span className="kb-muted">Loading counts…</span>
          ) : stats ? (
            <>
              <strong>{stats.total}</strong>
              <span className="kb-muted"> total demo users</span> ·{' '}
              <strong>{stats.eligible}</strong>
              <span className="kb-muted"> eligible to sweep at this cutoff</span>
            </>
          ) : null}
        </div>
        <Button
          type="button"
          variant="danger"
          disabled={busy || !stats || stats.eligible === 0}
          onClick={() => void sweep()}
        >
          {busy ? 'Sweeping…' : `Sweep ${stats?.eligible ?? 0}`}
        </Button>
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Audit log tab (partner+)
//
// Reverse-chronological feed over audit_event with substring filters on
// actor email + action. Backs the partner's "who did what when" question
// without forcing them into psql. Pure read; no destructive actions.
// --------------------------------------------------------------------------

function AuditTab() {
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState<string>(sevenAgo);
  const [to, setTo] = useState<string>(today);
  const [events, setEvents] = useState<AdminAuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      adminApi
        .listAudit({
          actor: actor.trim() || undefined,
          action: action.trim() || undefined,
          from: from || undefined,
          to: to || undefined,
          limit: 200,
        })
        .then((r) => {
          if (!alive) return;
          setEvents(r.events);
          setTotal(r.total);
          setError(null);
        })
        .catch((e: unknown) => {
          if (!alive) return;
          setError(e instanceof Error ? e.message : 'Failed to load audit log.');
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [actor, action, from, to]);

  return (
    <Card>
      <CardTitle>Audit log</CardTitle>
      <CardSubtitle>
        Append-only system feed — every mutating request lands here. Showing {events.length} of{' '}
        {total} match{total === 1 ? '' : 'es'}. Filters are case-insensitive substring matches.
      </CardSubtitle>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gap: 10,
          alignItems: 'flex-end',
          marginTop: '0.875rem',
          padding: '0.875rem',
          background: 'var(--kb-gray-50, #f8fafc)',
          borderRadius: 8,
        }}
      >
        <Field label="Actor email">
          <Input
            type="search"
            placeholder="cust@…"
            value={actor}
            onChange={(e) => setActor(e.currentTarget.value)}
          />
        </Field>
        <Field label="Action">
          <Input
            type="search"
            placeholder="onboarding, archive, sweep…"
            value={action}
            onChange={(e) => setAction(e.currentTarget.value)}
          />
        </Field>
        <Field label="From">
          <Input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.currentTarget.value)}
          />
        </Field>
        <Field label="To">
          <Input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.currentTarget.value)}
          />
        </Field>
      </div>
      {error && (
        <div className="kb-form-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
        {loading && !events.length ? (
          <p className="kb-muted">Loading…</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--kb-gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '6px' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Actor</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Action</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Target</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Client</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--kb-gray-100)' }}>
                  <td
                    style={{
                      padding: '6px',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {e.createdAt.replace('T', ' ').slice(0, 19)}
                  </td>
                  <td style={{ padding: '6px' }}>
                    {e.actorEmail ?? <span className="kb-muted">({e.actorKind})</span>}
                  </td>
                  <td
                    style={{
                      padding: '6px',
                      fontFamily: 'var(--kb-mono, ui-monospace, monospace)',
                    }}
                  >
                    {e.action}
                  </td>
                  <td
                    style={{
                      padding: '6px',
                      fontFamily: 'var(--kb-mono, ui-monospace, monospace)',
                      fontSize: 11,
                    }}
                  >
                    {e.targetTable
                      ? `${e.targetTable}${e.targetId ? `/${e.targetId.slice(0, 8)}` : ''}`
                      : '—'}
                  </td>
                  <td style={{ padding: '6px' }}>{e.clientDisplayName ?? '—'}</td>
                  <td style={{ padding: '6px' }}>
                    {e.status === 'success' ? (
                      '✓'
                    ) : (
                      <span style={{ color: 'var(--kb-red-600, #b91c1c)' }}>{e.status}</span>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && events.length === 0 && (
                <tr>
                  <td colSpan={6} className="kb-muted" style={{ padding: '12px 6px' }}>
                    No events match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Feature flags (platform_admin only)
//
// Runtime config the operator can flip without a deploy. Backing store is
// the feature_flag table (key text pk, value jsonb, description). Values
// are parsed as JSON in the editor so booleans, strings, numbers, lists
// and objects all work — whatever the consumer of the flag is shaped to
// read. See apps/api/src/lib/feature-flags.ts for the server-side cache.
// --------------------------------------------------------------------------

function FeatureFlagsCard() {
  const [flags, setFlags] = useState<AdminFeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-form state
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('true');
  const [newDesc, setNewDesc] = useState('');

  const refresh = () => {
    setLoading(true);
    adminApi
      .listFlags()
      .then((r) => {
        setFlags(r.flags);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load flags.'))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    let parsedValue: unknown;
    try {
      parsedValue = JSON.parse(newValue);
    } catch {
      setError(`Value isn't valid JSON. Try true, false, "string", 42, [1,2], or {"k":1}.`);
      return;
    }
    setBusy(true);
    try {
      await adminApi.upsertFlag(newKey.trim(), parsedValue, newDesc.trim() || null);
      setNewKey('');
      setNewValue('true');
      setNewDesc('');
      setNotice('Flag saved.');
      refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(key: string) {
    if (
      !confirm(
        `Delete flag "${key}"? Code reading this flag falls back to its hard-coded default until you recreate it.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await adminApi.deleteFlag(key);
      setNotice(`Deleted "${key}".`);
      refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function editValue(flag: AdminFeatureFlag) {
    const current = JSON.stringify(flag.value);
    const next = prompt(`New JSON value for "${flag.key}":\n(current: ${current})`, current);
    if (next == null || next === current) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(next);
    } catch {
      setError(`"${next}" isn't valid JSON.`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await adminApi.upsertFlag(flag.key, parsed, flag.description ?? null);
      setNotice(`Updated "${flag.key}".`);
      refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Feature flags</CardTitle>
      <CardSubtitle>
        Runtime config. Reads cache for ~30s per Cloud Run replica; edits propagate immediately on
        this instance and within ~30s on the others. Keys are short slugs like{' '}
        <code>reports.cash_flow.enable</code> or <code>stripe.live_mode</code>; values are any JSON
        (booleans, strings, numbers, lists, objects).
      </CardSubtitle>
      {error && (
        <div className="kb-form-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="kb-toast kb-toast--success" role="status" style={{ marginTop: 8 }}>
          {notice}
        </div>
      )}
      <form
        onSubmit={add}
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr 2fr auto',
          gap: 10,
          alignItems: 'flex-end',
          marginTop: '0.875rem',
          padding: '0.875rem',
          background: 'var(--kb-gray-50, #f8fafc)',
          borderRadius: 8,
        }}
      >
        <Field label="Key">
          <Input
            type="text"
            value={newKey}
            onChange={(e) => setNewKey(e.currentTarget.value)}
            placeholder="reports.enable_v2"
            required
          />
        </Field>
        <Field label="Value (JSON)">
          <Input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.currentTarget.value)}
            placeholder="true"
            required
          />
        </Field>
        <Field label="Description (optional)">
          <Input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.currentTarget.value)}
            placeholder="gate the v2 reports rollout"
          />
        </Field>
        <Button type="submit" disabled={busy}>
          {busy ? '…' : 'Add / update'}
        </Button>
      </form>
      <div style={{ marginTop: '1rem' }}>
        {loading ? (
          <p className="kb-muted">Loading…</p>
        ) : flags.length === 0 ? (
          <p className="kb-muted">No flags yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--kb-gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '6px' }}>Key</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Value</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Description</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Updated</th>
                <th style={{ padding: '6px' }} />
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.key} style={{ borderBottom: '1px solid var(--kb-gray-100)' }}>
                  <td
                    style={{
                      padding: '6px',
                      fontFamily: 'var(--kb-mono, ui-monospace, monospace)',
                    }}
                  >
                    {f.key}
                  </td>
                  <td
                    style={{
                      padding: '6px',
                      fontFamily: 'var(--kb-mono, ui-monospace, monospace)',
                      fontSize: 12,
                    }}
                  >
                    {JSON.stringify(f.value)}
                  </td>
                  <td style={{ padding: '6px' }}>{f.description ?? '—'}</td>
                  <td style={{ padding: '6px', fontSize: 11 }}>
                    {f.updatedAt.slice(0, 10)}
                    {f.updatedBy ? ` · ${f.updatedBy}` : ''}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void editValue(f)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => void remove(f.key)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Referrals tab (platform_admin) — review commissions + run payouts
// --------------------------------------------------------------------------

function refUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function ReferralsTab() {
  const [pending, setPending] = useState<AdminReferralCommission[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminApi
      .listReferralCommissions('accrued')
      .then((r) => {
        setPending(r.commissions);
        setSelected(new Set());
      })
      .catch(() => setError('Could not load commissions.'))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const selectedTotal = pending
    .filter((c) => selected.has(c.id))
    .reduce((s, c) => s + c.commissionCents, 0);

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function decide(decision: 'approve' | 'hold') {
    if (selected.size === 0) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const r = await adminApi.decideReferralCommissions([...selected], decision);
      setNotice(`${r.updated} commission(s) ${decision}d.`);
      setReloadKey((k) => k + 1);
    } catch {
      setError('Action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function runPayouts() {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const r = await adminApi.runReferralPayouts();
      setNotice(
        `Payouts run: ${r.paidCount} of ${r.partnersConsidered} partner(s) paid, ${refUsd(r.totalPaidCents)} total.`,
      );
      setReloadKey((k) => k + 1);
    } catch {
      setError('Payout run failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Referral commissions — review before payout</CardTitle>
      <CardSubtitle>
        Approve or hold accrued commissions, then pay approved balances to partners by bank deposit.
      </CardSubtitle>
      {notice && (
        <p role="status" style={{ color: '#1f7a3f', margin: '8px 0' }}>
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="kb-form-error">
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap' }}>
        <Button size="sm" disabled={busy || selected.size === 0} onClick={() => decide('approve')}>
          Approve{selected.size ? ` (${refUsd(selectedTotal)})` : ''}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy || selected.size === 0}
          onClick={() => decide('hold')}
        >
          Hold
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={runPayouts}>
          Run payouts now
        </Button>
      </div>
      {loading ? (
        <p>Loading…</p>
      ) : pending.length === 0 ? (
        <p style={{ color: '#6e6c66' }}>No commissions pending review.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6e6c66', fontSize: 12 }}>
              <th style={{ padding: '6px 8px' }} />
              <th style={{ padding: '6px 8px' }}>Partner</th>
              <th style={{ padding: '6px 8px' }}>Commission</th>
              <th style={{ padding: '6px 8px' }}>Rate</th>
              <th style={{ padding: '6px 8px' }}>Accrued</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((c) => (
              <tr key={c.id} style={{ borderTop: '1px solid #eceadf' }}>
                <td style={{ padding: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                    aria-label={`Select commission for ${c.partnerEmail}`}
                  />
                </td>
                <td style={{ padding: '8px' }}>{c.partnerEmail}</td>
                <td style={{ padding: '8px' }}>{refUsd(c.commissionCents)}</td>
                <td style={{ padding: '8px' }}>{c.rateBps / 100}%</td>
                <td style={{ padding: '8px' }}>{new Date(c.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------------
// Collaborator seat settings (platform_admin)
// --------------------------------------------------------------------------
const SEAT_TIER_LABEL: Record<string, string> = {
  starter: 'Starter',
  professional_plus: 'Professional+',
  business_plus: 'Business+',
};

function SeatConfigPanel() {
  const [cfg, setCfg] = useState<SeatConfig | null>(null);
  const [feeDollars, setFeeDollars] = useState('');
  const [priceId, setPriceId] = useState('');
  const [priceIdAnnual, setPriceIdAnnual] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .getSeatConfig()
      .then((c) => {
        setCfg(c);
        setFeeDollars(c.extraSeatFeeCents != null ? (c.extraSeatFeeCents / 100).toString() : '');
        setPriceId(c.stripeSeatPriceId ?? '');
        setPriceIdAnnual(c.stripeSeatPriceIdAnnual ?? '');
      })
      .catch(() => setError('Could not load seat settings.'));
  }, []);

  if (!cfg) return null;

  function setIncluded(tier: string, value: number) {
    setCfg((c) =>
      c
        ? {
            ...c,
            tiers: c.tiers.map((t) => (t.tier === tier ? { ...t, includedSeats: value } : t)),
          }
        : c,
    );
  }

  async function save() {
    if (!cfg) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const feeCents = feeDollars.trim() === '' ? null : Math.round(parseFloat(feeDollars) * 100);
      await adminApi.updateSeatConfig({
        tiers: cfg.tiers,
        extraSeatFeeCents: Number.isFinite(feeCents as number) ? feeCents : null,
        stripeSeatPriceId: priceId.trim() || null,
        stripeSeatPriceIdAnnual: priceIdAnnual.trim() || null,
      });
      setNotice('Seat settings saved.');
    } catch {
      setError('Could not save seat settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Collaborator seats</CardTitle>
      <CardSubtitle>Included seats per plan, and the price for extra (paid) seats.</CardSubtitle>
      <div style={{ display: 'grid', gap: 12, marginTop: '0.75rem', maxWidth: 440 }}>
        {cfg.tiers.map((t) => (
          <Field key={t.tier} label={`${SEAT_TIER_LABEL[t.tier] ?? t.tier} — included seats`}>
            <Input
              type="number"
              min={0}
              value={String(t.includedSeats)}
              onChange={(e) =>
                setIncluded(t.tier, Math.max(0, parseInt(e.currentTarget.value || '0', 10)))
              }
            />
          </Field>
        ))}
        <Field
          label="Extra seat fee (USD / month)"
          hint="Reference only — the Stripe price below is what actually bills."
        >
          <Input
            value={feeDollars}
            onChange={(e) => setFeeDollars(e.currentTarget.value)}
            placeholder="15.00"
          />
        </Field>
        <Field
          label="Stripe seat price ID — monthly"
          hint="Recurring per-seat price for month-billed clients."
        >
          <Input
            value={priceId}
            onChange={(e) => setPriceId(e.currentTarget.value)}
            placeholder="price_…"
          />
        </Field>
        <Field
          label="Stripe seat price ID — annual"
          hint="Recurring per-seat price for year-billed clients (must be a yearly price)."
        >
          <Input
            value={priceIdAnnual}
            onChange={(e) => setPriceIdAnnual(e.currentTarget.value)}
            placeholder="price_…"
          />
        </Field>
      </div>
      {notice && (
        <p role="status" style={{ color: '#1f7a3f', marginTop: 8 }}>
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="kb-form-error">
          {error}
        </p>
      )}
      <div style={{ marginTop: '0.75rem' }}>
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save seat settings'}
        </Button>
      </div>
    </Card>
  );
}

function AiConfigPanel() {
  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .getAiConfig()
      .then(setCfg)
      .catch(() => setError('Could not load AI cost settings.'));
  }, []);

  if (!cfg) return null;

  function setCap(tier: string, value: string) {
    const usd = value.trim() === '' ? null : Number(value);
    setCfg((c) =>
      c
        ? {
            ...c,
            tiers: c.tiers.map((t) =>
              t.tier === tier
                ? { ...t, monthlyCapUsd: usd != null && Number.isFinite(usd) ? usd : null }
                : t,
            ),
          }
        : c,
    );
  }

  async function save() {
    if (!cfg) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await adminApi.updateAiConfig({ tiers: cfg.tiers });
      setNotice('AI cost settings saved.');
    } catch {
      setError('Could not save AI cost settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>AI cost caps</CardTitle>
      <CardSubtitle>
        Monthly AI-spend ceiling per plan (a safety valve — over the cap, documents aren&apos;t
        auto-processed until next cycle). Blank = unlimited.
      </CardSubtitle>
      <div style={{ display: 'grid', gap: 12, marginTop: '0.75rem', maxWidth: 440 }}>
        {cfg.tiers.map((t) => (
          <Field key={t.tier} label={`${SEAT_TIER_LABEL[t.tier] ?? t.tier} — monthly cap (USD)`}>
            <Input
              type="number"
              min={0}
              value={t.monthlyCapUsd == null ? '' : String(t.monthlyCapUsd)}
              placeholder="unlimited"
              onChange={(e) => setCap(t.tier, e.currentTarget.value)}
            />
          </Field>
        ))}
      </div>
      {notice && (
        <p role="status" style={{ color: '#1f7a3f', marginTop: 8 }}>
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="kb-form-error">
          {error}
        </p>
      )}
      <div style={{ marginTop: '0.75rem' }}>
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save AI cost settings'}
        </Button>
      </div>
    </Card>
  );
}
